import {
    type ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
    type VisibilityState
} from '@tanstack/react-table'
import {type ComponentProps, Fragment, type ReactNode, useEffect, useRef, useState} from 'react'
import {cn} from '@utilities/helpers'
import {ScrollArea, Scrollbar} from '../ui/scroll-area'
import {
    type Order,
    TableRoot,
    TableHeader,
    TableHeaderRow,
    TableHeaderCell,
    TableHeaderCellLabel,
    TableSortButton,
    TableResizer,
    TableBody,
    TableRow,
    TableCell,
    TableCellSkeleton,
    TableExpandableRow,
    TablePagination,
    type TablePaginationProps
} from '../ui/table-atoms'

// TS < 5.4 does not ship the built-in NoInfer utility type.
type NoInfer<T> = [T][T extends unknown ? 0 : never]

const DEFAULT_PAGE_LIMIT = 8

interface ColumnMeta<TSortKey extends string> {
    sortKey?: TSortKey
}

type ColumnSize = number | `${number}%`

export interface DataTableCellContext<TDataItem> {
    dataItem: TDataItem
    index: number
    isRowExpanded: boolean
    toggleExpanded: () => void
}

export interface DataTableProps<TDataItem, TSortKey extends string, TColumnId extends string> {
    data: TDataItem[]
    columns: {
        id: TColumnId
        header: ReactNode
        cell: (context: DataTableCellContext<TDataItem>) => ReactNode
        size: ColumnSize
        maxSize?: ColumnSize
        minSize?: ColumnSize
        sortKey?: TSortKey
    }[]
    order?: Order<TSortKey>
    setOrder?: (order?: Order<TSortKey>) => void
    getRowProps?: ({dataItem}: {dataItem: TDataItem}) => ComponentProps<typeof TableRow>
    /** Rendered after a row when that row is expanded (toggled via a cell's toggleExpanded). */
    expandedRow?: (dataItem: TDataItem) => ReactNode
    /** Always rendered after every row (no toggle). */
    subRow?: (dataItem: TDataItem) => ReactNode
    isLoading?: boolean
    pagination?: TablePaginationProps['pagination']
    columnPinning?: {left: NoInfer<TColumnId>[]; right: NoInfer<TColumnId>[]}
    columnVisibility?: Partial<Record<NoInfer<TColumnId>, boolean>> & VisibilityState
    emptyComponent?: ReactNode
}

const retrieveColumnSize = ({size, tableWidth}: {size?: ColumnSize; tableWidth: number}) => {
    if (typeof size === 'string') {
        return (+size.replace('%', '') * tableWidth) / 100
    }
    return size
}

const makeReactTableColumns = <TDataItem, TSortKey extends string, TColumnId extends string>({
    columns,
    tableWidth
}: {
    columns: DataTableProps<TDataItem, TSortKey, TColumnId>['columns']
    tableWidth: number
}): ColumnDef<TDataItem>[] => {
    const columnHelper = createColumnHelper<TDataItem>()

    return columns.map(column =>
        columnHelper.display({
            id: column.id,
            header: () => column.header,
            cell: cell =>
                column.cell({
                    dataItem: cell.row.original,
                    index: cell.row.index,
                    isRowExpanded: cell.row.getIsExpanded(),
                    toggleExpanded: () => cell.row.toggleExpanded()
                }),
            size: retrieveColumnSize({size: column.size, tableWidth}),
            maxSize: retrieveColumnSize({size: column.maxSize, tableWidth}),
            minSize: retrieveColumnSize({size: column.minSize, tableWidth}),
            enableResizing: !!column.minSize || !!column.maxSize,
            meta: {
                sortKey: column.sortKey
            } as const satisfies ColumnMeta<TSortKey>
        })
    )
}

const getNextSortOrder = <TSortKey extends string>(
    currentOrder: Order<TSortKey> | undefined,
    sortKey: TSortKey
): Order<TSortKey> => {
    const isSameColumn = currentOrder?.orderBy === sortKey

    if (!(isSameColumn && currentOrder?.orderDirection)) {
        return {orderBy: sortKey, orderDirection: 'desc'}
    }

    if (currentOrder.orderDirection === 'desc') {
        return {orderBy: sortKey, orderDirection: 'asc'}
    }

    return {orderBy: undefined, orderDirection: undefined}
}

function DataTable<TDataItem, TSortKey extends string, TColumnId extends string>({
    data,
    columns,
    order,
    setOrder,
    getRowProps,
    expandedRow,
    subRow,
    isLoading,
    pagination,
    columnPinning = {left: [], right: []},
    columnVisibility,
    emptyComponent
}: DataTableProps<TDataItem, TSortKey, TColumnId>) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const [tableWidth, setTableWidth] = useState(0)

    const table = useReactTable({
        data,
        columnResizeMode: 'onChange',
        columns: makeReactTableColumns({columns, tableWidth}),
        getCoreRowModel: getCoreRowModel(),
        enableExpanding: !!expandedRow,
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            columnPinning,
            columnVisibility: columnVisibility ?? {}
        }
    })

    const {rows} = table.getRowModel()

    useEffect(() => {
        const wrapperElement = wrapperRef.current
        if (wrapperElement == null) {
            return
        }

        const handleResize = () => {
            setTableWidth(wrapperElement.clientWidth)
        }
        const resizeObserver = new ResizeObserver(handleResize)
        resizeObserver.observe(wrapperElement)
        setTableWidth(wrapperElement.clientWidth)

        return () => resizeObserver.disconnect()
    }, [])

    const renderTableContent = () => {
        if (!isLoading && rows.length === 0) {
            return emptyComponent
        }

        return (
            <div className="w-full min-w-0" ref={wrapperRef}>
                <ScrollArea
                    className="w-full"
                    scrollbar={
                        <>
                            <Scrollbar orientation="horizontal" />
                            <Scrollbar orientation="vertical" />
                        </>
                    }
                >
                    <TableRoot>
                        <TableHeader>
                            {table.getHeaderGroups().map(headerGroup => (
                                <TableHeaderRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        const sortKey = (header.column.columnDef.meta as ColumnMeta<TSortKey>)?.sortKey
                                        const pinningPosition = header.column.getIsPinned()

                                        return (
                                            <TableHeaderCell
                                                colSpan={header.colSpan}
                                                key={header.id}
                                                pinningPosition={pinningPosition}
                                                pinningStart={header.column.getStart(pinningPosition)}
                                                style={{width: header.getSize()}}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <TableHeaderCellLabel>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </TableHeaderCellLabel>
                                                )}
                                                {!!sortKey && (
                                                    <TableSortButton
                                                        onClick={() => {
                                                            const nextOrder = getNextSortOrder(order, sortKey)
                                                            setOrder?.(nextOrder)
                                                        }}
                                                        orderDirection={
                                                            sortKey === order?.orderBy && order?.orderDirection
                                                                ? order?.orderDirection
                                                                : null
                                                        }
                                                    />
                                                )}
                                                {header.column.getCanResize() && (
                                                    <TableResizer
                                                        onDoubleClick={() => header.column.resetSize()}
                                                        onMouseDown={header.getResizeHandler()}
                                                        onTouchStart={header.getResizeHandler()}
                                                    />
                                                )}
                                            </TableHeaderCell>
                                        )
                                    })}
                                </TableHeaderRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading
                                ? Array.from({length: pagination?.limit ?? DEFAULT_PAGE_LIMIT}).map((_, rowIndex) =>
                                      table.getHeaderGroups().map(headerGroup => (
                                          <TableRow key={`skeleton-${headerGroup.id}-${rowIndex}`}>
                                              {headerGroup.headers.map(header => {
                                                  const pinningPosition = header.column.getIsPinned()
                                                  return (
                                                      <TableCell
                                                          key={header.id}
                                                          pinningPosition={pinningPosition}
                                                          pinningStart={header.column.getStart(pinningPosition)}
                                                      >
                                                          <TableCellSkeleton />
                                                      </TableCell>
                                                  )
                                              })}
                                          </TableRow>
                                      ))
                                  )
                                : rows.map((row, rowIndex) => {
                                      const rowProps = getRowProps?.({dataItem: row.original})
                                      const isLastRow = rowIndex === rows.length - 1

                                      return (
                                          <Fragment key={row.id}>
                                              <TableRow
                                                  {...rowProps}
                                                  className={cn(
                                                      rowProps?.onClick && 'cursor-pointer',
                                                      // Expanded content brings its own borders — drop this
                                                      // row's bottom border so they don't double up.
                                                      row.getIsExpanded() && 'not-last:[&>td]:border-b-0',
                                                      rowProps?.className
                                                  )}
                                              >
                                                  {row.getVisibleCells().map(cell => (
                                                      <TableCell
                                                          key={cell.id}
                                                          pinningPosition={cell.column.getIsPinned()}
                                                          pinningStart={cell.column.getStart(cell.column.getIsPinned())}
                                                      >
                                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                      </TableCell>
                                                  ))}
                                              </TableRow>
                                              {expandedRow && row.getIsExpanded() ? (
                                                  <TableExpandableRow
                                                      className={cn(!isLastRow && 'border-b')}
                                                      colSpan={row.getVisibleCells().length}
                                                      isExpanded
                                                  >
                                                      {expandedRow(row.original)}
                                                  </TableExpandableRow>
                                              ) : null}
                                              {subRow ? (
                                                  <TableRow>
                                                      <TableCell colSpan={row.getVisibleCells().length}>
                                                          {subRow(row.original)}
                                                      </TableCell>
                                                  </TableRow>
                                              ) : null}
                                          </Fragment>
                                      )
                                  })}
                        </TableBody>
                    </TableRoot>
                </ScrollArea>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-1">
            {renderTableContent()}
            {pagination && <TablePagination pagination={pagination} />}
        </div>
    )
}

export {DataTable}
